import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2, ZoomIn, Check, X } from "lucide-react";

const ASPECTS = [
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "1:1", value: 1 },
  { label: "3:1", value: 3 },
];

async function getCroppedBlob(imageSrc, area) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(area.width);
  canvas.height = Math.round(area.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    Math.round(area.x),
    Math.round(area.y),
    Math.round(area.width),
    Math.round(area.height),
    0,
    0,
    Math.round(area.width),
    Math.round(area.height)
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}

export default function ImageCropModal({ open, imageSrc, defaultAspect = 16 / 9, filename = "logo.png", busy = false, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(defaultAspect);
  const [areaPixels, setAreaPixels] = useState(null);

  const onComplete = useCallback((_area, px) => setAreaPixels(px), []);

  if (!open) return null;

  const confirm = async () => {
    if (!areaPixels) return;
    const blob = await getCroppedBlob(imageSrc, areaPixels);
    const file = new File([blob], filename, { type: "image/png" });
    onConfirm(file);
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4" data-testid="crop-modal">
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border overflow-hidden shadow-xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="font-heading font-semibold">Potong & Zoom Gambar</h3>
          <button onClick={onCancel} data-testid="crop-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={16} /></button>
        </div>

        <div className="relative w-full h-72 bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
            restrictPosition={false}
          />
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground mr-1">Rasio:</span>
            {ASPECTS.map((a) => (
              <button
                key={a.label}
                onClick={() => setAspect(a.value)}
                data-testid={`crop-aspect-${a.label}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${aspect === a.value ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}
              >
                {a.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <ZoomIn size={16} className="text-muted-foreground shrink-0" />
            <input
              type="range" min={1} max={4} step={0.01} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="crop-zoom"
              className="w-full accent-primary cursor-pointer"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCancel} data-testid="crop-cancel" className="px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60">Batal</button>
            <button onClick={confirm} disabled={busy || !areaPixels} data-testid="crop-confirm" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Gunakan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
